import { createSimpleContext } from "@mux/ui/context"
import { type Accessor, batch, createEffect, createMemo, onCleanup, type ParentProps, untrack } from "solid-js"
import { createStore } from "solid-js/store"
import { DevicePreference, persisted } from "@/storage/persist"
import { useCheckServerHealth, type ServerHealth } from "@/transport/server-health"
import { pageIsVisible } from "@/utils/network-schedule"
import { stripMobileSurface } from "@/utils/responsive-routing"

export type StoredProject = { id: string; directory: string; expanded: boolean; name?: string; worktree?: string }
type StoredServer = string | ServerConnection.HttpBase | ServerConnection.Http
/**
 * A server API that can bind itself to another machine. Declared at module
 * scope so the recursive type has a name outside the provider.
 */
type Scopable<T> = T & {
  /** The same API bound to another machine's connection. */
  scoped: (key: ServerConnection.Key, machineName?: string) => Scopable<T>
}
const HEALTH_POLL_INTERVAL_MS = 45_000
// One slow or dropped probe is not an outage. Retry quickly and only report
// offline after consecutive failures, so the UI does not flap between
// connected and disconnected chrome while the event stream is still flowing.
// Recovery is immediate on the first success.
const HEALTH_RETRY_DELAY_MS = 5_000
const HEALTH_OFFLINE_AFTER_FAILURES = 3

export function normalizeServerUrl(input: string) {
  const trimmed = input.trim()
  if (!trimmed) return
  const withProtocol = /^https?:\/\//.test(trimmed) ? trimmed : `http://${trimmed}`
  return withProtocol.replace(/\/+$/, "")
}

export function serverName(conn?: ServerConnection.Any, ignoreDisplayName = false) {
  if (!conn) return ""
  if (conn.displayName && !ignoreDisplayName) return conn.displayName
  return conn.http.url.replace(/^https?:\/\//, "").replace(/\/+$/, "")
}

function projectsKey(key: ServerConnection.Key) {
  if (!key) return ""
  if (key === "sidecar") return "local"
  if (isLocalHost(key)) return "local"
  return key
}

function isLocalHost(url: string) {
  const host = url.replace(/^https?:\/\//, "").split(":")[0]
  return host === "localhost" || host === "127.0.0.1"
}

function sameHttpConnection(current: StoredServer, next: ServerConnection.Http) {
  const existing: ServerConnection.Http =
    typeof current === "string"
      ? { type: "http", http: { url: current } }
      : "type" in current
        ? current
        : { type: "http", http: current }

  return (
    existing.displayName === next.displayName &&
    existing.http.url === next.http.url &&
    existing.http.identityURL === next.http.identityURL &&
    existing.http.expectedMachineName === next.http.expectedMachineName &&
    existing.http.username === next.http.username &&
    existing.http.password === next.http.password &&
    existing.http.accessToken === next.http.accessToken &&
    existing.http.accessTokenExpiresAt === next.http.accessTokenExpiresAt &&
    existing.http.e2eKey === next.http.e2eKey &&
    !!existing.http.prepare === !!next.http.prepare &&
    !!existing.http.recover === !!next.http.recover
  )
}

export namespace ServerConnection {
  type Base = { displayName?: string }

  export type HttpBase = {
    url: string
    // Fleet transport changes retain the registered URL as their existing UI/storage identity.
    identityURL?: string
    expectedMachineName?: string
    username?: string
    password?: string
    accessToken?: string
    accessTokenExpiresAt?: number
    /** The machine's key (backend/e2e): when set, requests are sealed to it end to end. */
    e2eKey?: string
    prepare?: (rejectedToken?: string) => Promise<HttpBase>
    recover?: (trigger?: "network-error" | "gateway-error" | "health-check" | "sse-error" | "sse-stall" | "pty-error" | "request-stall", signal?: AbortSignal, options?: { avoidCurrent?: boolean }) => Promise<HttpBase | undefined>
  }

  // Regular web connections
  export type Http = {
    type: "http"
    http: HttpBase
  } & Base

  export type Sidecar = {
    type: "sidecar"
    http: HttpBase
  } & (
    | // Regular desktop server
    { variant: "base" }
    // WSL server (windows only)
    | {
        variant: "wsl"
        distro: string
      }
  ) &
    Base

  // Remote server desktop can SSH into
  export type Ssh = {
    type: "ssh"
    host: string
    // SSH client exposes an HTTP server for the app to use as a proxy
    http: HttpBase
  } & Base

  export type Any =
    | Http
    // All these are desktop-only
    | (Sidecar | Ssh)

  export const key = (conn: Any): Key => {
    switch (conn.type) {
      case "http":
        return Key.make(conn.http.identityURL ?? conn.http.url)
      case "sidecar": {
        if (conn.variant === "wsl") return Key.make(`wsl:${conn.distro}`)
        return Key.make("sidecar")
      }
      case "ssh":
        return Key.make(`ssh:${conn.host}`)
    }
  }

  export type Key = string & { _brand: "Key" }
  export const Key = { make: (v: string) => v as Key }

  /** The machine a connection reaches. Token renewals and route changes replace the connection, not the machine. */
  export const machine = (http: HttpBase) => JSON.stringify([http.expectedMachineName, http.identityURL ?? http.url])
}

const serverContext = createSimpleContext({
  name: "Server",
  init: (props: { defaultServer: ServerConnection.Key; servers?: Array<ServerConnection.Any>; startInactive?: boolean }) => {
    const checkServerHealth = useCheckServerHealth()

    const [store, setStore, _, ready] = persisted(
      {
        ...DevicePreference.global("server.v4"),
        project(value) {
          if (!value || typeof value !== "object" || Array.isArray(value)) return value
          const { favorites: _favorites, lastProject: _lastProject, ...local } = value as typeof store
          return local
        },
      },
      createStore({
        list: [] as StoredServer[],
        favorites: {} as Record<string, string[]>,
        lastProject: {} as Record<string, string | undefined>,
        /**
         * Last machine-relative chat route (a chat, a new chat, or an Oracle), keyed
         * by machine route name. Absent when the machine was left on its home.
         */
        lastSessionRoute: {} as Record<string, string | undefined>,
        /** Last coding machine name (fleet display name), used when URL has no /m/:machine. */
        lastMachine: undefined as string | undefined,
      }),
    )
    const [transient, setTransient] = createStore<ServerConnection.Http[]>([])

    const url = (x: StoredServer) => (typeof x === "string" ? x : "type" in x ? x.http.url : x.url)

    const allServers = createMemo((): Array<ServerConnection.Any> => {
      const servers = [
        ...(props.servers ?? []),
        ...store.list.map((value) =>
          typeof value === "string"
            ? {
                type: "http" as const,
                http: { url: value },
              }
            : value,
        ),
        ...transient,
      ]

      const deduped = new Map(
        servers.map((value) => {
          const conn: ServerConnection.Any = "type" in value ? value : { type: "http", http: value }
          return [ServerConnection.key(conn), conn]
        }),
      )

      return [...deduped.values()]
    })

    const [state, setState] = createStore({
      active: props.startInactive ? undefined : (props.defaultServer as ServerConnection.Key | undefined),
      health: {} as Record<ServerConnection.Key, ServerHealth | undefined>,
    })

    function startHealthPolling() {
      let alive = true
      let busy = false
      let failures = 0
      let failing: ServerConnection.Key | undefined
      let timer: ReturnType<typeof setTimeout> | undefined

      const schedule = (delay = HEALTH_POLL_INTERVAL_MS) => {
        if (!alive || !pageIsVisible() || !state.active) return
        if (timer !== undefined) clearTimeout(timer)
        timer = setTimeout(run, delay)
      }

      const record = (key: ServerConnection.Key, result: ServerHealth) => {
        if (!alive || state.active !== key) return HEALTH_POLL_INTERVAL_MS
        if (result.healthy) {
          failures = 0
          failing = undefined
          setState("health", key, result)
          return HEALTH_POLL_INTERVAL_MS
        }
        if (failing !== key) {
          failing = key
          failures = 0
        }
        failures += 1
        if (failures >= HEALTH_OFFLINE_AFTER_FAILURES) setState("health", key, { healthy: false })
        return HEALTH_RETRY_DELAY_MS
      }

      const run = () => {
        if (timer !== undefined) clearTimeout(timer)
        timer = undefined
        if (!alive || !pageIsVisible() || !state.active) return
        if (busy) {
          schedule()
          return
        }
        const conn = current()
        // Fleet machines publish authoritative heartbeat state through the control
        // plane and keep a live event stream. Polling their tunnel duplicates both.
        // A fleet transport holds a grant or renews one through `prepare`.
        if (!conn || conn.http.accessToken || conn.http.prepare) {
          schedule()
          return
        }
        busy = true
        const key = ServerConnection.key(conn)
        void checkServerHealth(conn.http)
          .then(
            (result) => record(key, result),
            () => record(key, { healthy: false }),
          )
          .then((delay) => {
            busy = false
            schedule(delay)
          })
      }

      const onVisibility = () => {
        if (!pageIsVisible()) {
          if (timer !== undefined) clearTimeout(timer)
          timer = undefined
          return
        }
        run()
      }

      run()
      if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility)
      return () => {
        alive = false
        if (timer !== undefined) clearTimeout(timer)
        if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility)
      }
    }

    function setActive(input: ServerConnection.Key, options?: { machineName?: string }) {
      if (state.active !== input) setState("active", input)
      const name =
        options?.machineName ||
        serverName(allServers().find((s) => ServerConnection.key(s) === input)) ||
        undefined
      if (name && store.lastMachine !== name) setStore("lastMachine", name)
    }

    function clearActive() {
      if (state.active !== undefined) setState("active", undefined)
    }

    function rememberMachine(name: string | undefined) {
      if (!name) return
      if (store.lastMachine !== name) setStore("lastMachine", name)
    }

    function clearRememberedMachine() {
      if (store.lastMachine !== undefined) setStore("lastMachine", undefined)
    }

    function rememberSessionRoute(machineName: string, route: string) {
      if (!machineName || !route) return
      const next = stripMobileSurface(route)
      if (store.lastSessionRoute[machineName] !== next) setStore("lastSessionRoute", machineName, next)
    }

    function lastSessionRoute(machineName: string | undefined) {
      if (!machineName) return undefined
      const route = store.lastSessionRoute[machineName]
      return route ? stripMobileSurface(route) : route
    }

    function forgetSessionRoute(machineName: string) {
      if (store.lastSessionRoute[machineName] !== undefined) setStore("lastSessionRoute", machineName, undefined)
    }

    function clearSessionRoutes() {
      if (Object.keys(store.lastSessionRoute).length > 0) setStore("lastSessionRoute", {})
    }

    function add(input: ServerConnection.Http, options?: { activate?: boolean; persist?: boolean }) {
      const url_ = normalizeServerUrl(input.http.url)
      if (!url_) return
      const conn = { ...input, http: { ...input.http, url: url_ } }
      return batch(() => {
        if (options?.persist === false) {
          const persistedList = store.list.filter((x) => url(x) !== url_)
          if (persistedList.length !== store.list.length) setStore("list", persistedList)
          const existing = transient.findIndex((x) => ServerConnection.key(x) === ServerConnection.key(conn))
          if (existing !== -1) {
            if (!sameHttpConnection(transient[existing]!, conn)) setTransient(existing, conn)
          }
          else setTransient(transient.length, conn)
        } else {
          const existing = store.list.findIndex((x) => url(x) === url_)
          if (existing !== -1) {
            if (!sameHttpConnection(store.list[existing]!, conn)) setStore("list", existing, conn)
          } else {
            setStore("list", store.list.length, conn)
          }
        }
        if (options?.activate !== false) {
          setState("active", ServerConnection.key(conn))
          if (conn.displayName && store.lastMachine !== conn.displayName) {
            setStore("lastMachine", conn.displayName)
          }
        }
        return conn
      })
    }

    function remove(key: ServerConnection.Key) {
      const transientList = transient.filter((x) => ServerConnection.key(x) !== key)
      if (transientList.length !== transient.length) setTransient(transientList)
      const found = store.list.some((x) => url(x) === key)
      if (!found) {
        if (state.active === key) {
          const next = store.list[0]
          setState("active", next ? ServerConnection.Key.make(url(next)) : undefined)
        }
        return
      }

      const list = store.list.filter((x) => url(x) !== key)
      batch(() => {
        setStore("list", list)
        if (state.active === key) {
          const next = list[0]
          setState("active", next ? ServerConnection.Key.make(url(next)) : undefined)
        }
      })
    }

    const isReady = createMemo(() => ready())

    const check = (conn: ServerConnection.Any) => checkServerHealth(conn.http).then((x) => x.healthy)

    const connectionFor = (key: ServerConnection.Key | undefined) =>
      key ? allServers().find((s) => ServerConnection.key(s) === key) : undefined
    const current: Accessor<ServerConnection.Any | undefined> = createMemo(() => connectionFor(state.active))

    createEffect(() => {
      state.active
      current()
      onCleanup(untrack(startHealthPolling))
    })

    /**
     * The server API bound to one connection. The dashboard-wide value follows
     * the route's machine; a machine scope binds the same API to its own
     * machine, so code under it reads that machine whatever the route shows.
     */
    function view(
      activeKey: Accessor<ServerConnection.Key | undefined>,
      connection: Accessor<ServerConnection.Any | undefined>,
      bound: boolean,
      /** The machine's fleet name, which its routes use; a connection may carry a friendlier display name. */
      machineName?: string,
    ) {
      const origin = createMemo(() => projectsKey((activeKey() ?? "") as ServerConnection.Key))
      const isLocal = createMemo(() => {
        const c = connection()
        return (c?.type === "sidecar" && c.variant === "base") || (c?.type === "http" && isLocalHost(c.http.url))
      })
      const value = {
        ready: isReady,
        /**
         * True inside a machine scope: this API names one machine whatever the
         * route shows. False for the dashboard-wide API, which follows the route.
         */
        bound,
        healthy: () => {
          const key = activeKey()
          return key ? state.health[key]?.healthy : undefined
        },
        health: (key: ServerConnection.Key) => state.health[key],
        check,
        isLocal,
        get key() {
          return activeKey()
        },
        get name() {
          return machineName || serverName(connection())
        },
        get list() {
          return allServers()
        },
        get current() {
          return connection()
        },
        setActive,
        clearActive,
        rememberMachine,
        clearRememberedMachine,
        rememberSessionRoute,
        lastSessionRoute,
        forgetSessionRoute,
        clearSessionRoutes,
        get lastMachine() {
          return store.lastMachine
        },
        add,
        remove,
        projects: {
          list: () => [],
          open(_id: string, _directory: string, _name?: string) {},
          close(_id: string) {},
          expand(_id: string) {},
          collapse(_id: string) {},
          last() {
            const key = origin()
            if (!key) return
            return store.lastProject[key]
          },
          touch(id: string) {
            const key = origin()
            if (!key) return
            setStore("lastProject", key, id)
          },
          favorites() {
            return store.favorites[origin()] ?? []
          },
          isFavorite(id: string) {
            return (store.favorites[origin()] ?? []).includes(id)
          },
          toggleFavorite(id: string) {
            const key = origin()
            if (!key) return
            const list = store.favorites[key] ?? []
            if (list.includes(id)) {
              setStore("favorites", key, list.filter((item) => item !== id))
              return
            }
            setStore("favorites", key, [id, ...list])
          },
          restoreFavorites(input: string[]) {
            const key = origin()
            if (!key) return
            setStore("favorites", key, input)
          },
          restore(input: { projects: unknown[]; last?: string | null }) {
            const key = origin()
            if (!key) return
            batch(() => {
              setStore("lastProject", key, input.last ?? undefined)
            })
          },
        },
      }
      return value
    }

    type ServerView = Scopable<ReturnType<typeof view>>
    const scoped = (key: ServerConnection.Key, machineName?: string): ServerView =>
      Object.assign(view(() => key, createMemo(() => connectionFor(key)), true, machineName), { scoped })
    return Object.assign(view(() => state.active, current, false), { scoped })
  },
})

export const useServer = serverContext.use
export const ServerProvider = serverContext.provider

/**
 * Binds the server API to one machine for everything below it, so machine-scoped
 * state reads that machine's connection even while the route shows another.
 */
export function MachineServerProvider(props: ParentProps<{ machineKey: ServerConnection.Key; machineName?: string }>) {
  const view = useServer().scoped(props.machineKey, props.machineName)
  return <serverContext.context.Provider value={view}>{props.children}</serverContext.context.Provider>
}

/** Optional server access for helpers that may run outside the dashboard tree. */
export function tryUseServer(): ReturnType<typeof useServer> | undefined {
  try {
    return useServer()
  } catch {
    return undefined
  }
}
